import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Search, Bell, Menu, X, LogIn, Cast } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCast } from '../context/CastContext';
import ProfileDropdown from './ProfileDropdown';
import SiteLogo from './SiteLogo';
import './Navbar.css';

const Navbar = ({ onLoginClick, onPreloadRoute }) => {
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const inputRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Cast is initialised once by CastProvider: the navbar only reflects it.
  const {
    isAvailable: isCastAvailable,
    isConnected: isCastConnected,
    deviceName: castDeviceName,
    requestSession: requestCastSession,
    stopCasting
  } = useCast();

  // Clear search and close mobile menu when going back to Home or changing routes
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (location.pathname === '/') {
        setSearchText('');
        setSearchOpen(false);
      }
      setMobileMenuOpen(false);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && !event.target.closest('.mobile-menu-toggle')) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSearch = () => {
    if (searchOpen) {
      setSearchOpen(false);
      setSearchText('');
    } else {
      setSearchOpen(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleSearchCheck = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter' && searchText.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchText)}`);
    }
  };

  const handleCastClick = () => {
    if (isCastConnected) {
      stopCasting().catch((castError) => console.error('[Cast] Session error:', castError));
      return;
    }
    // Shows the Chromecast picker; cancellation and errors are surfaced by CastProvider.
    requestCastSession().catch(() => {});
  };

  const navIntentProps = (path) => ({
    onMouseEnter: () => onPreloadRoute?.(path),
    onFocus: () => onPreloadRoute?.(path),
    onPointerDown: () => onPreloadRoute?.(path),
    onTouchStart: () => onPreloadRoute?.(path)
  });

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-left">
        <NavLink to="/" className="logo-link">
          <SiteLogo className="logo" />
        </NavLink>
      </div>

      <ul className="nav-links">
        <li><NavLink to="/" end {...navIntentProps('/')} className={({ isActive }) => isActive ? "active" : ""}>Accueil</NavLink></li>
        <li><NavLink to="/movies" {...navIntentProps('/movies')} className={({ isActive }) => isActive ? "active" : ""}>Films</NavLink></li>
        <li><NavLink to="/series" {...navIntentProps('/series')} className={({ isActive }) => isActive ? "active" : ""}>Séries</NavLink></li>
        <li><NavLink to="/live" {...navIntentProps('/live')} className={({ isActive }) => isActive ? "active" : ""}>Direct</NavLink></li>
        <li><NavLink to="/mylist" {...navIntentProps('/mylist')} className={({ isActive }) => isActive ? "active" : ""}>Ma Liste</NavLink></li>
      </ul>

      <div className="navbar-right">
        <div className={`search-box ${searchOpen ? 'open' : ''}`}>
          <button className="search-icon-btn" onClick={toggleSearch}>
            <Search size={20} />
          </button>
          <input
            ref={inputRef}
            type="text"
            placeholder="Titres, personnes, genres"
            value={searchText}
            onChange={handleSearchCheck}
            onKeyDown={handleSearchSubmit}
            onBlur={() => !searchText && setSearchOpen(false)}
          />
        </div>
        <div className="nav-icon notification-icon">
          <Bell size={20} />
          <span className="notification-badge"></span>
        </div>

        {isCastAvailable && (
          <button
            className={`nav-icon cast-btn ${isCastConnected ? 'connected' : ''}`}
            onClick={handleCastClick}
            title={isCastConnected ? `Arrêter la diffusion sur ${castDeviceName || 'Chromecast'}` : 'Caster sur la TV'}
            aria-label={isCastConnected ? `Arrêter la diffusion sur ${castDeviceName || 'Chromecast'}` : 'Caster sur la TV'}
          >
            <Cast size={20} />
            {isCastConnected && <span className="cast-connected-dot"></span>}
          </button>
        )}

        {user ? (
          <ProfileDropdown />
        ) : (
          <button className="login-btn-nav" onClick={onLoginClick}>
            <LogIn size={20} />
            <span>Se connecter</span>
          </button>
        )}

        <button
          className="mobile-menu-toggle nav-icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Drawer */}
      <div className={`mobile-drawer ${mobileMenuOpen ? 'open' : ''}`} ref={mobileMenuRef}>
        <div className="drawer-header">
          <SiteLogo className="logo" />
          <button className="close-drawer" onClick={() => setMobileMenuOpen(false)}>
            <X size={24} />
          </button>
        </div>
        <ul className="drawer-links">
          <li><NavLink to="/" end {...navIntentProps('/')} onClick={() => setMobileMenuOpen(false)}>Accueil</NavLink></li>
          <li><NavLink to="/movies" {...navIntentProps('/movies')} onClick={() => setMobileMenuOpen(false)}>Films</NavLink></li>
          <li><NavLink to="/series" {...navIntentProps('/series')} onClick={() => setMobileMenuOpen(false)}>Séries</NavLink></li>
          <li><NavLink to="/live" {...navIntentProps('/live')} onClick={() => setMobileMenuOpen(false)}>Direct</NavLink></li>
          <li><NavLink to="/mylist" {...navIntentProps('/mylist')} onClick={() => setMobileMenuOpen(false)}>Ma Liste</NavLink></li>
          {user?.role === 'admin' && (
            <li><NavLink to="/admin" onClick={() => setMobileMenuOpen(false)}>Admin</NavLink></li>
          )}
        </ul>
        {!user && (
          <div className="drawer-footer">
            <button className="login-btn-nav full-width" onClick={() => { setMobileMenuOpen(false); onLoginClick(); }}>
              Se connecter
            </button>
          </div>
        )}
      </div>
      {mobileMenuOpen && <div className="drawer-overlay" onClick={() => setMobileMenuOpen(false)}></div>}
    </nav>
  );
};

export default Navbar;
